import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  CheckCircleIcon,
  SparkleIcon,
  LightningIcon,
  HandCoinsIcon,
  FireIcon,
  CrownSimpleIcon,
  LifebuoyIcon,
  FilmReelIcon,
  MagicWandIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const PERKS = [
  {
    icon: FilmReelIcon,
    title: "Cinema mode — true 4K streaming",
    titleSuffix: "Yearly only",
    body: "Stream your live show in buttery-smooth 4K with adaptive bitrate that auto-fits every viewer's network. Auto-recorded so replays just work. Standard sellers stream in WebRTC — Plus sellers stream in cinema.",
    headline: true,
  },
  {
    icon: SparkleIcon,
    title: "Bidoria on every stream",
    titleSuffix: "Yearly only",
    body: "9 voiced personalities — Auctioneer, Screamer, Sportscaster, Wrestler, Sotheby's, and more — that hype your auctions in real time. Add custom catchphrases. The only marketplace with this.",
    headline: true,
  },
  {
    icon: MagicWandIcon,
    title: "Auto-Detect & List",
    titleSuffix: "Yearly only",
    body: "Show any item on camera — cards, sneakers, comics, Funkos — and Bidoria spots it in 2 seconds, then offers a one-tap popup with a clean title, category, and market-comp price. Hit Yes and it's live in your shop. List 10× faster, never miss an item.",
    headline: true,
  },
  {
    icon: HandCoinsIcon,
    title: "1% cashback credits",
    body: "Every paid purchase returns 1% as Bidorah credits. Spend them at checkout or stack for bigger drops.",
  },
  {
    icon: FireIcon,
    title: "2× streak rewards",
    body: "Double the login-streak payouts: $10 at 7 days, $50 at 30. Keep your streak, keep the cash.",
  },
  {
    icon: LightningIcon,
    title: "Early access to streams",
    body: "Join scheduled shows 5 minutes before they open to the public. First in line, first to bid.",
  },
  {
    icon: CrownSimpleIcon,
    title: "Plus pill on your profile",
    body: "Stand out in chat and on your collector profile with a member-only pill that sellers recognize.",
  },
  {
    icon: LifebuoyIcon,
    title: "Priority support",
    body: "Your tickets jump the queue. Real humans, faster replies.",
  },
  {
    icon: SparkleIcon,
    title: "Cancel anytime",
    body: "Month-to-month or yearly. Cancel with one click — your perks stay until the period ends.",
  },
];

export default function Plus() {
  const { user, refreshUser } = useAuth() || {};
  const [plan, setPlan] = useState("yearly");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get("/plus/status");
        setStatus(r.data);
      } catch {}
    })();
  }, [user]);

  const onCheckout = async () => {
    if (!user) {
      window.location.href = "/login?next=/plus";
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/plus/checkout", {
        plan,
        origin_url: window.location.origin,
      });
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Could not start checkout");
        setBusy(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  const onCancel = async () => {
    if (!window.confirm("Cancel Bidorah Plus? You keep perks until the end of your billing period."))
      return;
    setCancelling(true);
    try {
      await api.post("/plus/cancel", {});
      toast.success("Plus will end at the billing period.");
      const r = await api.get("/plus/status");
      setStatus(r.data);
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
    setCancelling(false);
  };

  const isPlus = !!status?.is_plus;

  const monthly = status?.plans?.monthly || { price_cents: 2999 };
  const yearly = status?.plans?.yearly || { price_cents: 29900 };
  const monthlyAnnualized = (monthly.price_cents * 12) / 100;
  const yearlyPrice = yearly.price_cents / 100;
  const savings = Math.round((1 - yearlyPrice / monthlyAnnualized) * 100);

  const active = plan === "yearly" ? yearly : monthly;
  const activeLabel =
    plan === "yearly"
      ? `$${(yearly.price_cents / 100).toFixed(0)} / year`
      : `$${(monthly.price_cents / 100).toFixed(2)} / month`;

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="plus-page">
      <Navbar />
      <main className="flex-1">
        <section className="bg-gradient-to-br from-[#0F3C57] via-[#1D78AB] to-[#2DB4D1] text-white py-20 px-6">
          <div className="max-w-5xl mx-auto text-center">
            <p className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-bold tracking-widest uppercase">
              <CrownSimpleIcon size={14} weight="fill" className="text-amber-300" />
              Bidorah Plus
            </p>
            <h1 className="mt-6 font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              Stream in 4K.
              <br />
              Win more bids.
            </h1>
            <p className="mt-5 text-white/85 max-w-2xl mx-auto">
              The only marketplace where sellers get <strong>true 4K Cinema-mode live shows</strong>, <strong>Bidoria — the 9-voice co-host</strong> that hypes every bid, and <strong>Auto-Detect &amp; List</strong> that turns whatever's in your hand into a live listing in seconds. Buyers get cashback, early access, and 2× streak credits. One membership. Both sides win.
            </p>

            {isPlus && (
              <div className="mt-10 bg-white/10 backdrop-blur rounded-2xl p-6 max-w-xl mx-auto text-left" data-testid="plus-active-card">
                <p className="text-xs font-bold tracking-widest text-amber-300">ACTIVE MEMBER</p>
                <p className="mt-1 text-2xl font-display font-black">
                  You're on the {status?.plan} plan.
                </p>
                {status?.current_period_end && (
                  <p className="text-sm text-white/80 mt-1">
                    Renews {new Date(status.current_period_end).toLocaleDateString()}
                    {status.cancel_at_period_end ? " · cancelling at period end" : ""}
                  </p>
                )}
                {!status?.cancel_at_period_end && (
                  <button
                    onClick={onCancel}
                    disabled={cancelling}
                    className="mt-4 text-xs underline opacity-80 hover:opacity-100"
                    data-testid="plus-cancel-btn"
                  >
                    {cancelling ? "Cancelling…" : "Cancel Plus"}
                  </button>
                )}
              </div>
            )}

            {!isPlus && (
              <>
                <div className="mt-10 inline-flex rounded-full bg-white/10 backdrop-blur p-1 gap-1" data-testid="plus-plan-toggle">
                  <button
                    className={`px-5 py-2 text-sm font-bold rounded-full transition ${
                      plan === "monthly"
                        ? "bg-white text-[#0F3C57]"
                        : "text-white/80 hover:text-white"
                    }`}
                    onClick={() => setPlan("monthly")}
                    data-testid="plus-plan-monthly"
                  >
                    Monthly
                  </button>
                  <button
                    className={`px-5 py-2 text-sm font-bold rounded-full transition flex items-center gap-2 ${
                      plan === "yearly"
                        ? "bg-white text-[#0F3C57]"
                        : "text-white/80 hover:text-white"
                    }`}
                    onClick={() => setPlan("yearly")}
                    data-testid="plus-plan-yearly"
                  >
                    Yearly
                    <span className="bg-amber-400 text-[#0F3C57] px-2 py-0.5 rounded-full text-[10px] font-black">
                      Save {savings}%
                    </span>
                  </button>
                </div>

                <div className="mt-6 text-6xl font-display font-black tracking-tighter">
                  {plan === "yearly" ? "$299" : "$29.99"}
                  <span className="text-xl text-white/70 font-bold">
                    /{plan === "yearly" ? "yr" : "mo"}
                  </span>
                </div>
                <p className="text-white/70 text-xs mt-1" data-testid="plus-price-caption">
                  {plan === "yearly"
                    ? `That's $${(yearlyPrice / 12).toFixed(2)}/mo — saves $${(monthlyAnnualized - yearlyPrice).toFixed(0)}`
                    : `Or go yearly for $${yearlyPrice} (save ${savings}%)`}
                </p>

                <button
                  onClick={onCheckout}
                  disabled={busy}
                  data-testid="plus-checkout-btn"
                  className="mt-8 inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-50 text-[#0F3C57] px-8 py-4 rounded-full font-black text-lg transition shadow-xl"
                >
                  <CrownSimpleIcon size={20} weight="fill" />
                  {busy ? "Redirecting to Stripe…" : `Go Plus — ${activeLabel}`}
                </button>
                <p className="text-xs text-white/60 mt-3">
                  Secure checkout by Stripe. Cancel anytime.
                </p>
              </>
            )}
          </div>
        </section>

        {/* FOUNDER-TRINITY HEADLINER — the three perks no other marketplace has.
            Cinema · Bidoria · Auto-Detect. Each card has its own micro-preview
            visualization so the value reads at a glance, plus an animated
            ambient glow that drifts behind the cards. */}
        <section
          className="bg-zinc-950 text-white border-y border-amber-500/20 relative overflow-hidden"
          data-testid="plus-trinity"
        >
          {/* Slow-drifting aurora layer. Pure CSS, GPU-cheap. */}
          <div className="absolute inset-0 pointer-events-none bidorah-trinity-aurora" />
          <div className="max-w-6xl mx-auto px-6 py-16 sm:py-20 relative">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-400/30 px-3 py-1 text-[11px] uppercase tracking-widest text-amber-300 font-bold mb-5"
                data-testid="plus-trinity-tag"
              >
                <SparkleIcon size={11} weight="fill" />
                Yearly-only · Pristine Perks
              </span>
              <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
                Three perks no other<br />
                <span className="text-amber-300">marketplace can match.</span>
              </h2>
              <p className="mt-4 text-white/70 text-base sm:text-lg">
                Tools that turn a phone into a broadcast studio. The reason serious sellers stay.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 items-stretch">
              {/* CINEMA 4K */}
              <div
                className="group relative rounded-3xl bg-zinc-900/70 border border-amber-500/25 p-7 sm:p-8 overflow-hidden backdrop-blur-sm hover:border-amber-400/60 transition-all duration-500 flex flex-col"
                data-testid="plus-trinity-cinema"
              >
                <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-amber-500/15 blur-3xl group-hover:bg-amber-500/25 transition" />
                <div className="relative flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-10 w-10 rounded-xl bg-amber-400/15 border border-amber-400/30 grid place-items-center">
                      <FilmReelIcon size={20} weight="fill" className="text-amber-300" />
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-amber-300/80 font-bold">
                      Cinema 4K
                    </span>
                  </div>
                  <h3 className="font-display font-black text-2xl sm:text-[26px] tracking-tight leading-[1.05]">
                    Stream in <span className="text-amber-300">true 4K</span>.
                  </h3>
                  <p className="mt-3 text-sm sm:text-[15px] text-white/75 leading-relaxed">
                    Broadcast-grade 4K with adaptive bitrate that auto-fits every viewer's network. Auto-recorded so replays just work — no editor, no upload.
                  </p>
                  {/* Mini-preview: resolution ladder */}
                  <div className="mt-auto pt-5">
                    <div className="rounded-xl bg-black/50 border border-amber-300/15 p-3.5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-widest text-amber-300/70 font-bold">
                        Quality ladder
                      </span>
                      <span className="inline-flex items-center gap-1 text-[10px] font-black text-amber-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-300 animate-pulse" />
                        LIVE
                      </span>
                    </div>
                    {[
                      { l: "2160p", w: "100%", a: true },
                      { l: "1440p", w: "78%" },
                      { l: "1080p", w: "60%" },
                      { l: "720p",  w: "42%" },
                    ].map((q) => (
                      <div key={q.l} className="flex items-center gap-2 py-0.5">
                        <span className={`text-[10px] font-bold tabular-nums w-10 ${q.a ? "text-amber-300" : "text-white/45"}`}>
                          {q.l}
                        </span>
                        <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${q.a ? "bg-amber-400" : "bg-white/20"}`}
                            style={{ width: q.w }}
                          />
                        </div>
                      </div>
                    ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* BIDORIA */}
              <div
                className="group relative rounded-3xl bg-zinc-900/70 border border-amber-500/25 p-7 sm:p-8 overflow-hidden backdrop-blur-sm hover:border-amber-400/60 transition-all duration-500 flex flex-col"
                data-testid="plus-trinity-bidoria"
              >
                <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full bg-amber-400/15 blur-3xl group-hover:bg-amber-400/25 transition" />
                <div className="relative flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-10 w-10 rounded-xl bg-amber-400/15 border border-amber-400/30 grid place-items-center">
                      <SparkleIcon size={20} weight="fill" className="text-amber-300" />
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-amber-300/80 font-bold">
                      Bidoria · 9 voices
                    </span>
                  </div>
                  <h3 className="font-display font-black text-2xl sm:text-[26px] tracking-tight leading-[1.05]">
                    A co-host that <span className="text-amber-300">never tires.</span>
                  </h3>
                  <p className="mt-3 text-sm sm:text-[15px] text-white/75 leading-relaxed">
                    Bidoria hypes every bid, welcomes viewers, drops market comps, and keeps the show's energy up — even when you're solo.
                  </p>
                  {/* Mini-preview: voice quote with waveform */}
                  <div className="mt-auto pt-5">
                    <div className="rounded-xl bg-black/50 border border-amber-300/15 p-3.5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-widest text-amber-300/70 font-bold">
                        Screamer · live
                      </span>
                      <span className="inline-flex items-end gap-[2px] h-3">
                        {[3,7,5,9,4,8,6,5,7,4,8].map((h, i) => (
                          <span
                            key={i}
                            className="w-[2px] rounded-sm bg-amber-300"
                            style={{
                              height: `${h * 1.2}px`,
                              opacity: 0.55 + (h / 20),
                              animation: `bidorah-wave 1.1s ease-in-out ${i * 0.07}s infinite alternate`,
                            }}
                          />
                        ))}
                      </span>
                    </div>
                    <p className="text-[12px] text-white/90 leading-snug italic">
                      "CARDKING DROPS EIGHT-FIFTY—GOT-EIGHT-FIFTY—WHO'S-GOT-NINE? CLOSING FAST!"
                    </p>
                    <p className="mt-2 text-[10px] text-white/40">
                      Generated live · ~3s end-to-end
                    </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* AUTO-DETECT */}
              <div
                className="group relative rounded-3xl bg-zinc-900/70 border border-amber-500/25 p-7 sm:p-8 overflow-hidden backdrop-blur-sm hover:border-amber-400/60 transition-all duration-500 flex flex-col"
                data-testid="plus-trinity-detect"
              >
                <div className="absolute -bottom-24 -right-20 w-64 h-64 rounded-full bg-amber-500/15 blur-3xl group-hover:bg-amber-500/25 transition" />
                <div className="relative flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-10 w-10 rounded-xl bg-amber-400/15 border border-amber-400/30 grid place-items-center">
                      <MagicWandIcon size={20} weight="fill" className="text-amber-300" />
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-amber-300/80 font-bold">
                      Auto-Detect &amp; List
                    </span>
                  </div>
                  <h3 className="font-display font-black text-2xl sm:text-[26px] tracking-tight leading-[1.05]">
                    List <span className="text-amber-300">10× faster.</span>
                  </h3>
                  <p className="mt-3 text-sm sm:text-[15px] text-white/75 leading-relaxed">
                    Show any item on camera. Bidoria spots it in 2 seconds and offers a one-tap popup with a clean title, category, and market-comp price.
                  </p>
                  {/* Mini-preview: detected card popup */}
                  <div className="mt-auto pt-5">
                    <div className="rounded-xl bg-black/50 border border-amber-300/15 p-3.5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-widest text-amber-300/70 font-bold">
                        Detected · 1.8s
                      </span>
                      <span className="inline-flex items-center gap-1 text-[10px] font-black text-emerald-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        match
                      </span>
                    </div>
                    <div className="rounded-lg bg-gradient-to-br from-amber-400/10 to-amber-600/0 border border-amber-400/25 p-2.5">
                      <p className="text-[12px] font-black text-white leading-tight">
                        2023 Topps Chrome — Acuña Jr.
                      </p>
                      <div className="mt-1 flex items-center justify-between">
                        <span className="text-[10px] text-white/60">Sports Cards · Auction</span>
                        <span className="text-[12px] font-black text-amber-300 tabular-nums">$42</span>
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-1.5">
                      <div className="text-center text-[10px] font-bold text-white/85 py-1.5 rounded-md bg-amber-400/10 border border-amber-400/30">
                        Yes, list it
                      </div>
                      <div className="text-center text-[10px] font-bold text-white/45 py-1.5 rounded-md bg-white/5 border border-white/10">
                        Skip
                      </div>
                    </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 px-6 max-w-6xl mx-auto" data-testid="plus-perks">
          <h2 className="text-center overline text-zinc-500">WHAT YOU UNLOCK</h2>
          <p className="text-center font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
            Every perk pays for itself.
          </p>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PERKS.map((p, i) => {
              const Icon = p.icon;
              const headline = p.headline;
              return (
                <div
                  key={i}
                  className={`rounded-2xl border p-6 transition ${
                    headline
                      ? "border-amber-300 bg-gradient-to-br from-amber-50 to-white hover:shadow-xl hover:shadow-amber-200/40 sm:col-span-2 lg:col-span-1"
                      : "border-zinc-200 hover:shadow-lg hover:border-[#1D78AB]/30"
                  }`}
                  data-testid={`plus-perk-${i}`}
                >
                  <div
                    className={`h-12 w-12 rounded-xl text-white grid place-items-center ${
                      headline
                        ? "bg-gradient-to-br from-amber-400 to-amber-600"
                        : "bg-gradient-to-br from-[#1D78AB] to-[#0F3C57]"
                    }`}
                  >
                    <Icon size={22} weight="fill" />
                  </div>
                  <h3 className="mt-4 font-display font-black text-lg sm:text-xl flex items-baseline gap-1.5 flex-nowrap">
                    <span className="truncate">{p.title}</span>
                    {p.titleSuffix && (
                      <span
                        className="bidorah-yearly-pill text-[9px] uppercase tracking-wider font-bold text-amber-800 px-1.5 py-0.5 rounded-full whitespace-nowrap shrink-0 relative overflow-hidden"
                        data-testid="plus-yearly-pill"
                      >
                        <span className="relative z-10">{p.titleSuffix}</span>
                      </span>
                    )}
                  </h3>
                  <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="py-16 px-6 bg-zinc-50" data-testid="plus-faq">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-display font-black text-3xl tracking-tighter text-center">
              Questions, answered.
            </h2>
            <div className="mt-8 space-y-4 text-sm">
              <Q q="How do I get my 1% cashback?">
                Every time you complete a paid order on Bidorah, your account is credited with 1% of the sale price in Bidorah credits. Credits appear in your Dashboard wallet within seconds.
              </Q>
              <Q q="Can I cancel?">
                Yes — cancel from this page in one click. You keep Plus perks until the end of your current billing period.
              </Q>
              <Q q="What if I buy a yearly plan and cancel later?">
                Yearly is non-refundable, but you keep Plus perks for the full 12 months.
              </Q>
              <Q q="Does Plus work for wholesale buyers?">
                Yes — all perks (cashback, streak, early access) work identically for retail and wholesale accounts.
              </Q>
            </div>
          </div>
        </section>

        {!isPlus && (
          <section className="py-16 px-6 text-center">
            <p className="font-display font-black text-4xl tracking-tighter">
              Join Bidorah Plus today.
            </p>
            <button
              onClick={onCheckout}
              disabled={busy}
              data-testid="plus-checkout-btn-bottom"
              className="mt-6 inline-flex items-center gap-2 bg-[#1D78AB] hover:bg-[#0F3C57] text-white px-8 py-4 rounded-full font-black text-lg transition"
            >
              <CrownSimpleIcon size={20} weight="fill" />
              Go Plus — {activeLabel}
            </button>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Q({ q, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full p-4 flex items-center justify-between text-left"
      >
        <span className="font-bold text-zinc-900">{q}</span>
        <span className="text-zinc-400">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="px-4 pb-4 text-zinc-600">{children}</div>}
    </div>
  );
}
